export const ensureDocumentStructure = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const docEl = document.documentElement;
  if (!docEl) {
    return;
  }

  if (!document.head) {
    const head = document.createElement('head');
    docEl.insertBefore(head, document.body || null);
  }

  if (!document.body) {
    const body = document.createElement('body');
    docEl.appendChild(body);
  }
};

export const patchDocumentAccessors = (): void => {
  if (typeof document === 'undefined') return;

  const docProto = Document.prototype as any;
  if (docProto.__documentAccessorsPatched__) return;
  docProto.__documentAccessorsPatched__ = true;

  // rc-util/dynamicCSS.js getContainer() does document.querySelector('head') || document.body.
  // During page teardown both can return null, causing findStyles(null).children to crash.
  // We cache the last known non-null head/body so the fallbacks always resolve to a real node.
  const originalQuerySelector: (selector: string) => Element | null = docProto.querySelector;
  let cachedHead: Element | null = originalQuerySelector.call(document, 'head');

  docProto.querySelector = function patchedQuerySelector(selector: string): Element | null {
    const result = originalQuerySelector.call(this, selector);
    if (result) {
      if (selector === 'head') cachedHead = result;
      return result;
    }
    if (selector === 'head' && cachedHead) return cachedHead;
    return null;
  };

  const bodyDescriptor = Object.getOwnPropertyDescriptor(Document.prototype, 'body');
  if (bodyDescriptor?.get) {
    const originalBodyGet = bodyDescriptor.get;
    let cachedBody: HTMLElement | null = originalBodyGet.call(document) as HTMLElement | null;

    Object.defineProperty(Document.prototype, 'body', {
      configurable: true,
      get(): HTMLElement | null {
        const el = originalBodyGet.call(this) as HTMLElement | null;
        if (el) {
          cachedBody = el;
          return el;
        }
        return cachedBody;
      },
    });
  }
};

export const patchRemoveChildNotFound = (): void => {
  const patchedFlag = '__removeChildNotFoundPatched__';
  const nodeCtor = (globalThis as { Node?: { prototype?: Node } }).Node;
  const nodeProto = nodeCtor?.prototype as (Node & { [patchedFlag]?: boolean }) | undefined;

  if (!nodeProto || nodeProto[patchedFlag]) return;

  const originalRemoveChild = nodeProto.removeChild;

  nodeProto.removeChild = function removeChildPatched<T extends Node>(child: T): T {
    try {
      return originalRemoveChild.call(this, child) as T;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotFoundError') {
        return child;
      }
      throw error;
    }
  };

  nodeProto[patchedFlag] = true;
};
